var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { throttle } from "lodash";
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
const initialState = {
    count: 0,
    data: [],
    element: null,
    hasMore: true,
    loading: true,
    page: 0,
    total: 0
};
var ActionType;
(function (ActionType) {
    ActionType["AppendResult"] = "APPEND_RESULT";
    ActionType["LoadingStart"] = "LOADING_START";
    ActionType["Reset"] = "RESET";
    ActionType["SetElement"] = "SET_ELEMENT";
})(ActionType || (ActionType = {}));
const appendResult = (result, pagination) => ({
    type: ActionType.AppendResult,
    payload: { result, pagination }
});
const loadingStartAction = () => ({ type: ActionType.LoadingStart });
const resetAction = () => ({ type: ActionType.Reset });
const setElementAction = (virtual, className) => ({
    type: ActionType.SetElement,
    payload: {
        element: document.querySelector(className !== null && className !== void 0 ? className : (virtual ? ".ant-table-tbody-virtual-holder" : ".ant-table-body"))
    }
});
function reducer(state, action) {
    switch (action.type) {
        case ActionType.AppendResult:
            if (state.page === action.payload.result.page) {
                return state;
            }
            return Object.assign(Object.assign(Object.assign({}, state), action.payload.result), { loading: false, data: action.payload.pagination
                    ? action.payload.result.data
                    : state.data.concat(action.payload.result.data) });
        case ActionType.LoadingStart:
            return state.loading ? state : Object.assign(Object.assign({}, state), { loading: true });
        case ActionType.Reset:
            return Object.assign({}, initialState);
        case ActionType.SetElement:
            return state.element === action.payload.element
                ? state
                : Object.assign(Object.assign({}, state), { element: action.payload.element });
        default:
            return state;
    }
}
function initState(state) {
    return state;
}
/**
 * Infinite scroll hook meant to be used with a table, or other components that has a container of list items with the same height.
 */
export function useInfiniteScroll({ className, containerHeight, fetchMoreDelay = 500, getMore, limit = 50, overscan = 100, pagination = false, virtual, initialFetch = true }) {
    const [state, dispatch] = useReducer(reducer, initialState, initState);
    const loading = useRef(false);
    const { data, hasMore, page, element } = state;
    const reset = useCallback(() => {
        dispatch(resetAction());
    }, []);
    useEffect(() => {
        if (element) {
            return;
        }
        const interval = setInterval(() => {
            dispatch(setElementAction(virtual, className));
        }, 500);
        return () => {
            clearInterval(interval);
        };
    }, [data.length, element, virtual, className]);
    const getRemainingHeight = useCallback(() => {
        if (!element) {
            return 0;
        }
        return Math.max(element.scrollHeight - containerHeight - element.scrollTop, 0);
    }, [containerHeight, element]);
    const loadMore = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (loading.current || !hasMore) {
            return;
        }
        dispatch(loadingStartAction());
        loading.current = true;
        const result = yield getMore({ limit, page: page + 1 });
        loading.current = false;
        dispatch(appendResult(result, pagination));
    }), [getMore, hasMore, limit, page, pagination]);
    const throttledLoadMore = useMemo(() => throttle(loadMore, fetchMoreDelay, { leading: true, trailing: false }), [loadMore, fetchMoreDelay]);
    // Load initial data
    useEffect(() => {
        if (!initialFetch) {
            return;
        }
        if (!element && data.length > 0) {
            return;
        }
        const remainingHeight = getRemainingHeight();
        if (!(hasMore && overscan >= remainingHeight)) {
            return;
        }
        throttledLoadMore();
    }, [
        data,
        hasMore,
        throttledLoadMore,
        overscan,
        getRemainingHeight,
        element,
        initialFetch
    ]);
    const onScroll = useCallback((event) => {
        event.preventDefault();
        const { clientHeight, scrollHeight, scrollTop } = event.target;
        const remainingHeight = scrollHeight - (scrollTop + clientHeight);
        if (remainingHeight > overscan) {
            return;
        }
        throttledLoadMore();
    }, [throttledLoadMore, overscan]);
    const throttledOnScroll = useMemo(() => throttle(onScroll, fetchMoreDelay, { leading: false, trailing: true }), [fetchMoreDelay, onScroll]);
    return Object.assign(Object.assign({}, state), { onScroll: throttledOnScroll, reset });
}
